/**
 * Canned Responses Page
 * Manage reusable response templates for support tickets
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Plus, Search, Pencil, Trash2, MessageSquareText } from 'lucide-react';

interface CannedResponse {
  id: string;
  title: string;
  content: string;
  category: string | null;
  shortcut: string | null;
  isShared: boolean;
  usageCount: number;
  createdAt: string;
  updatedAt: string;
}

export function CannedResponsesPage() {
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string | undefined>();
  const [page, setPage] = useState(1);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingResponse, setEditingResponse] = useState<CannedResponse | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingResponse, setDeletingResponse] = useState<CannedResponse | null>(null);

  // Form state
  const [formTitle, setFormTitle] = useState('');
  const [formContent, setFormContent] = useState('');
  const [formCategory, setFormCategory] = useState('');
  const [formShortcut, setFormShortcut] = useState('');
  const [formShared, setFormShared] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['cannedResponses', { search, category: categoryFilter, page }],
    queryFn: async () => {
      const result = await api.getCannedResponses({
        search: search || undefined,
        category: categoryFilter,
        page,
        limit: 20,
      });
      return result;
    },
  });

  const responses = (data?.data || []) as CannedResponse[];
  const pagination = data?.pagination;

  // Extract unique categories from responses for the filter dropdown
  const categories = Array.from(
    new Set(responses.map((r) => r.category).filter(Boolean))
  ) as string[];

  function invalidateResponses() {
    queryClient.invalidateQueries({ queryKey: ['cannedResponses'] });
  }

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => api.createCannedResponse(data),
    onSuccess: () => {
      invalidateResponses();
      closeDialog();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      api.updateCannedResponse(id, data),
    onSuccess: () => {
      invalidateResponses();
      closeDialog();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteCannedResponse(id),
    onSuccess: () => {
      invalidateResponses();
      setDeleteDialogOpen(false);
      setDeletingResponse(null);
    },
  });

  function openCreateDialog() {
    setEditingResponse(null);
    setFormTitle('');
    setFormContent('');
    setFormCategory('');
    setFormShortcut('');
    setFormShared(false);
    setDialogOpen(true);
  }

  function openEditDialog(response: CannedResponse) {
    setEditingResponse(response);
    setFormTitle(response.title);
    setFormContent(response.content);
    setFormCategory(response.category || '');
    setFormShortcut(response.shortcut || '');
    setFormShared(response.isShared);
    setDialogOpen(true);
  }

  function openDeleteDialog(response: CannedResponse) {
    setDeletingResponse(response);
    setDeleteDialogOpen(true);
  }

  function closeDialog() {
    setDialogOpen(false);
    setEditingResponse(null);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    const payload: Record<string, unknown> = {
      title: formTitle,
      content: formContent,
      category: formCategory || null,
      shortcut: formShortcut || null,
      isShared: formShared,
    };

    if (editingResponse) {
      updateMutation.mutate({ id: editingResponse.id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  }

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Canned Responses</h1>
          <p className="text-muted-foreground">Manage reusable response templates</p>
        </div>
        <Button onClick={openCreateDialog}>
          <Plus className="h-4 w-4 mr-2" />
          New Response
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search responses..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="pl-10"
          />
        </div>
        <Select
          value={categoryFilter || 'all'}
          onValueChange={(v) => {
            setCategoryFilter(v === 'all' ? undefined : v);
            setPage(1);
          }}
        >
          <SelectTrigger className="w-48">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map((cat) => (
              <SelectItem key={cat} value={cat}>
                {cat}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Responses Table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : responses.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <MessageSquareText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-medium mb-2">No canned responses found</h3>
            <p className="text-sm text-muted-foreground mb-4">
              {search || categoryFilter
                ? 'Try adjusting your search or filters'
                : 'Create your first canned response to get started'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Shortcut</TableHead>
                  <TableHead className="text-center">Usage</TableHead>
                  <TableHead className="text-center">Visibility</TableHead>
                  <TableHead className="w-24">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {responses.map((response) => (
                  <TableRow
                    key={response.id}
                    className="cursor-pointer"
                    onClick={() => openEditDialog(response)}
                  >
                    <TableCell className="font-medium">{response.title}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {response.category || '-'}
                    </TableCell>
                    <TableCell>
                      {response.shortcut ? (
                        <code className="text-xs bg-muted px-1.5 py-0.5 rounded">
                          {response.shortcut}
                        </code>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center text-sm">{response.usageCount}</TableCell>
                    <TableCell className="text-center">
                      <Badge variant={response.isShared ? 'default' : 'secondary'}>
                        {response.isShared ? 'Shared' : 'Private'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(response)}
                        >
                          <Pencil className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openDeleteDialog(response)}
                        >
                          <Trash2 className="h-3 w-3 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 pt-4">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
              >
                Previous
              </Button>
              <span className="text-sm text-muted-foreground">
                Page {page} of {pagination.totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page === pagination.totalPages}
                onClick={() => setPage(page + 1)}
              >
                Next
              </Button>
            </div>
          )}
        </>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent key={editingResponse?.id || 'new'} className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingResponse ? 'Edit Canned Response' : 'New Canned Response'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Title *</Label>
              <Input
                value={formTitle}
                onChange={(e) => setFormTitle(e.target.value)}
                placeholder="e.g. Welcome Greeting"
                required
              />
            </div>

            <div>
              <Label>Content *</Label>
              <Textarea
                value={formContent}
                onChange={(e) => setFormContent(e.target.value)}
                placeholder="Write your response template here..."
                rows={8}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <Input
                  value={formCategory}
                  onChange={(e) => setFormCategory(e.target.value)}
                  placeholder="e.g. Greetings"
                />
              </div>
              <div>
                <Label>Shortcut</Label>
                <Input
                  value={formShortcut}
                  onChange={(e) => setFormShortcut(e.target.value)}
                  placeholder="e.g. /greeting"
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="shared-checkbox"
                checked={formShared}
                onChange={(e) => setFormShared(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300"
              />
              <Label htmlFor="shared-checkbox" className="mb-0 cursor-pointer">
                Shared (visible to all agents)
              </Label>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={closeDialog}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSaving || !formTitle || !formContent}>
                {isSaving ? 'Saving...' : editingResponse ? 'Update Response' : 'Create Response'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Canned Response</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{deletingResponse?.title}</strong>? This
              action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deletingResponse && deleteMutation.mutate(deletingResponse.id)}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete Response'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
